import React, { useMemo, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Normalize } from 'styled-normalize';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { msalInstance } from '@/auth';
import { queryClient } from '@/config/reactQuery';
import { FeatureFlagsProvider } from '@/feature-flags';
import { TranslationsProvider } from '@/translations';
import { GlobalStyle } from '@/ui/globalStyles';

import { AppCustomerContext } from '@/AppCustomerContext';
import { UserPermissionModel } from '@/types';
import { AppThemeProvider } from './providers/AppThemeProvider';
import { HttpInterceptor } from './providers/HttpInterceptor';
import { GoogleAnalytics } from './providers/GoogleAnalytics';
import { App } from './App';
import { CommsAppShell } from './CommsAppShell';

export const AppWithProviders = () => {
  const [router] = useState(() =>
    createBrowserRouter([
      {
        path: '*',
        element: (
          <CommsAppShell>
            <GoogleAnalytics>
              <App />
            </GoogleAnalytics>
          </CommsAppShell>
        ),
      },
    ]),
  );
  const [customerIds, setCustomerIds] = useState<number[]>([]);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissionModel>({
    loaded: false,
  });

  const appCustomerContext = useMemo(
    () => ({
      customerIds,
      setCustomerIds,
      locationIds,
      setLocationIds,
      userPermissions,
      setUserPermissions,
    }),
    [customerIds, locationIds, userPermissions],
  );

  return (
    <React.StrictMode>
      <FeatureFlagsProvider>
        <MsalProvider instance={msalInstance}>
          <AppThemeProvider>
            <GlobalStyle />
            <QueryClientProvider client={queryClient}>
              <TranslationsProvider>
                <HttpInterceptor>
                  <AppCustomerContext.Provider value={appCustomerContext}>
                    <Normalize />
                    <RouterProvider router={router} />
                  </AppCustomerContext.Provider>
                </HttpInterceptor>
              </TranslationsProvider>
            </QueryClientProvider>
          </AppThemeProvider>
        </MsalProvider>
      </FeatureFlagsProvider>
    </React.StrictMode>
  );
};
